import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Carousel} from 'react-responsive-carousel';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';

import CommentsWrapper from '@/components/comments/Wrapper';
import ContentsModal from '@/components/common/ContentsModal';
import CommunityFooter from '@/components/community/contents/CommunityFooter';
import {timeForToday, switchNewLineTxt} from '@/utils';
import NoBrandLogo from '@/assets/banner/brand-default.svg';
import ContentsDeleteAlert from '@/components/common/ContentsDeleteAlert';
import emptyKnewnew from '@/products/user_default.svg';
import {
  CommunityContent,
  CommunityContentId,
  EditableCommunity,
  isOpenCommunityLoginModal,
  CommunityCommentCount,
  CommunityLikeCount,
  CommunityIsLike,
  UserInfo,
} from '@/store';

import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import styles from './styles.module.scss';

interface SliderImage {
  url: string;
}
interface CategoryItemType {
  txt: Category;
  param: CategoryParam;
}

export default function CommunityContents() {
  const navigator = useNavigate();
  const setEditableCommunity = useSetRecoilState(EditableCommunity);
  const setOpenCommunityLogin = useSetRecoilState(isOpenCommunityLoginModal);
  const setCommunityCommentCount = useSetRecoilState(CommunityCommentCount);
  const setCommunityLikeCount = useSetRecoilState(CommunityLikeCount);
  const setCommunityIsLike = useSetRecoilState(CommunityIsLike);
  const [author, setAuthor] = useState<Author>();
  const [brand, setBrand] = useState<BrandInfo>();
  const [contentsId, setContentsId] = useRecoilState(CommunityContentId);
  const [contents, setContents] = useRecoilState(
    CommunityContent({
      id: contentsId,
    }),
  );
  const [images, setImages] = useState<SliderImage[]>([]);
  const userInfo = useRecoilValue(UserInfo);
  const categories: CategoryItemType[] = [
    {
      txt: 'Special',
      param: 'special',
    },
    {
      txt: 'Family',
      param: 'family',
    },
    {
      txt: 'Refresh',
      param: 'refresh',
    },
    {
      txt: 'Eco-friendly',
      param: 'eco_friendly',
    },
    {
      txt: 'Pet-friendly',
      param: 'pet_friendly',
    },
    {
      txt: 'Healthy',
      param: 'healthy',
    },
    {
      txt: 'Drinking',
      param: 'drinking',
    },
    {
      txt: 'Daily',
      param: 'daily',
    },
  ];

  const makeCapital = (title: string | undefined) => {
    if (title) {
      const words = title.split('');
      words[0] = words[0].toUpperCase();
      return words.join('');
    }
    return '';
  };

  const moveToProfile = ($event: React.MouseEvent<HTMLDivElement>) => {
    $event.stopPropagation();
    navigator(
      author?.id === userInfo.id ? '/user' : `/community/writer/${author?.id}`,
    );
  };

  const updatePage = () => {
    const currentItem = document.querySelector('.slider');
    currentItem?.querySelector('.slide.selected');
  };

  const init = async () => {
    setAuthor(undefined);
    setBrand(undefined);
    setImages([]);
    const path = window.location.pathname.split('/');
    const id = path[path.length - 1];
    setContentsId(Number(id));
    if (userInfo.id === -1) {
      setOpenCommunityLogin(true);
    }
  };

  const moveToEdit = async () => {
    const path = window.location.pathname.split('/');
    const id = path[path.length - 1];
    const param: CommunityContentReq = {
      title: contents?.title as string,
      content: contents?.content as string,
      brands: contents?.brands.id as number,
      brandItem: contents?.brands,
      addresStr: `${contents?.address1} ${contents?.address2}`,
      address1: -1,
      address2: -1,
      category: contents?.category as Category,
      images: contents?.images as ImageObj[],
    };
    await setEditableCommunity(param);
    navigator(`/community/editor/${id}`);
  };

  useEffect(() => {
    init();
    scrollTo(0, 0);
    if (contents) {
      setImages(
        contents.images.map(o => {
          return {
            url: o.image,
          };
        }),
      );
      setAuthor(contents.author);
      setBrand(contents.brands);
      setCommunityCommentCount(contents.commentCount);
      setCommunityLikeCount(contents.likeCount);
      setCommunityIsLike(contents.isLike);
    }
    () => setContentsId(0);
  }, [contents]);

  return (
    <div className="community-detail-contents">
      <div className="community-detail-wrapper">
        <div className="community-detail-header">
          <div className="community-detail-imgs contents-body">
            {images.length > 0 && (
              <Carousel
                showArrows={false}
                showStatus={true}
                showThumbs={false}
                showIndicators={false}
                infiniteLoop={false}
                emulateTouch={true}
                onSwipeEnd={() => updatePage()}
                statusFormatter={(c, t) => {
                  return `${c} / ${t}`;
                }}>
                {images.map((o, i) => {
                  return (
                    <div key={Math.random()} data-order={`${i + 1}`}>
                      <img
                        src={`${o.url}`}
                        alt=""
                        style={{minWidth: '100%', height: '468px'}}
                      />
                    </div>
                  );
                })}
              </Carousel>
            )}
          </div>
        </div>
        <div className="contents-info">
          <div
            className="writer"
            onClick={($event: React.MouseEvent<HTMLDivElement>) => {
              if (author) moveToProfile($event);
            }}>
            <div
              className="profile"
              style={{
                background: `url(${
                  author?.profileImage ? author?.profileImage : emptyKnewnew
                }) no-repeat center / cover`,
              }}></div>
            {author ? (
              <div className="name">{author?.nickname}</div>
            ) : (
              <div className="delete-name"> {'탈퇴한 유저입니다'}</div>
            )}
          </div>
          <div className="content-category">
            {categories.find(c => c.param === contents?.category)?.txt}
          </div>
        </div>
        <div className="contents-title">
          <div className="title">{contents?.title}</div>
          <div className="contents-sub-info">
            {timeForToday(
              contents?.created ? contents?.created.split('.')[0] : '',
            )}{' '}
            •{' '}
            {contents?.address1
              ? contents?.address1 + ' ' + contents?.address2
              : '온라인 스토어'}
          </div>
        </div>
        <div
          className="detail-contents"
          dangerouslySetInnerHTML={{
            __html: contents ? switchNewLineTxt(contents.content || '') : '',
          }}></div>
        <div className="brands-divider"></div>
        <div className="brands">
          <div
            className="brands-info"
            style={{
              cursor: brand?.parentId ? 'pointer' : 'default',
              width: '100%',
            }}
            onClick={() =>
              brand?.parentId
                ? navigator(`/brand/${brand?.parentId}/${brand?.id}`)
                : undefined
            }>
            <div
              className="brands-logo"
              style={{
                background: `url(${
                  brand?.logoImage && brand?.logoImage.length > 0
                    ? brand?.logoImage
                    : NoBrandLogo
                }) no-repeat center / cover`,
              }}></div>
            {brand?.parentId ? (
              <div className={`brand-txt ${styles.brandLink}`}>
                <div>
                  <div className="name" style={{textAlign: 'left'}}>
                    {brand?.title}
                  </div>
                  <div className="category" style={{textAlign: 'left'}}>
                    {brand.categoryList
                      ? makeCapital(
                          brand.categoryList.join(' ∙ ').replaceAll(' / ', '/'),
                        )
                      : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="non-verified-brand-txt">
                <div className="name">{brand?.title}</div>
              </div>
            )}
          </div>
          {brand?.parentId ? (
            <div
              className={styles.arrowWrapper}
              style={{
                cursor: brand?.parentId ? 'pointer' : 'default',
              }}
              onClick={() =>
                brand?.parentId
                  ? navigator(`/brand/${brand?.parentId}/${brand?.id}`)
                  : undefined
              }>
              <span className={styles.arrowIcon}></span>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="divider"></div>
        <CommentsWrapper
          id={contents ? contents.id : -1}
          commentCnt={contents ? contents.commentCount : 0}
        />
        <CommunityFooter
          id={contents ? contents.id : -1}
          contents={contents}
          setter={(c: Community) => setContents(c)}
        />
        <ContentsModal
          type={
            userInfo && author?.id === userInfo.id
              ? 'contents-edit'
              : 'contents-report'
          }
          fetch={() => init()}
          moveToEdit={() => moveToEdit()}
        />
        <ContentsDeleteAlert />
      </div>
    </div>
  );
}
